import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';
import { getRegions } from '@luxuryescapes/lib-regions';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useQuery from '~/hooks/useQuery';

import {
  InternalPromoWithDiscounts,
  PromoLoyaltyMetaConfig,
  PromoMeta,
  PromoProductTypes,
  PromoRegionGroupings,
  PromoWarning,
  createOrUpdatePromoDiscount,
  deletePromoDiscount,
  getMinSpendProductTypeOptions,
  getProductTypeOptions,
  getPromoTypeDisplayName,
  mapLoyaltyPointsBonusConfigToV1,
} from '~/services/PromoService';

import { sortBy } from '~/utils/arrayUtils';
import currencyFormatter from '~/utils/currencyFormatter';
import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

import HeaderInfo from '../Common/HeaderInfo';
import Spinner from '../Common/Spinner';
import DebugModal from '../DebugModal/DebugModal';
import Accordion from '../v2/common/Accordion/Accordion';

import LoyaltyPointsBonusConfigSummary from './LoyaltyPointsBonusConfigSummary';
import { getProductTypeEligibilityHelperTitle } from './ProductTypeEligibilityHelper';
import PromoDiscountEditModal from './PromoDiscountEditModal';
import { copyPromoDiscount } from './promoDiscountCopyUtils';

/**
 * @deprecated (define in backend)
 */
const REGION_SORT_ORDER = [
  'Australia',
  'New Zealand',
  'United States',
  'United Kingdom',
  'Singapore',
  'India',
  'Germany',
  'Italy',
  'Canada',
  'China',
  'France',
  'Hong Kong',
  'Indonesia',
  'Ireland',
  'Israel',
  'Japan',
  'Korea',
  'Macau',
  'Malaysia',
  'Netherlands',
  'Philippines',
  'Qatar',
  'Russia',
  'Saudi Arabia',
  'South Africa',
  'Spain',
  'Taiwan',
  'Thailand',
  'United Arab Emirates',
  'Vietnam',
];

type RegionOption = {
  name: string;
  code: string;
  message?: string;
};

const getRegionOptions = (extraRegionOptions: PromoRegionGroupings): Array<RegionOption> => {
  return extraRegionOptions
    .map(
      (region): RegionOption => ({
        name: region.code,
        code: region.code,
        message: `${region.code} - includes ${region.regionCodes.join(', ')}`,
      }),
    )
    .concat(
      getRegions().map(
        (region): RegionOption => ({
          name: region.name,
          code: region.code,
        }),
      ),
    );
};

const getSortedRegions = (extraRegionOptions: PromoRegionGroupings) =>
  sortBy(
    getRegionOptions(extraRegionOptions),
    (region) => {
      const regionOrder = REGION_SORT_ORDER.indexOf(region.name);
      if (regionOrder === -1) {
        // Put it at the bottom of the list if it's not in the desired order
        return 999;
      }
      return regionOrder;
    },
    'asc',
  );

export const getPromoDiscountColumnVisibilityModel = (
  promoCode: InternalPromoWithDiscounts | null,
  promoDiscountData: Array<App.PromoDiscount> | null,
): GridColumnVisibilityModel => {
  const model: GridColumnVisibilityModel = {
    min_spend: false,
    max_discount: false,
    discount_value: false,
    products: false,
    subscription_item_discount_value: false,
    subscription_item_discount_type: false,
    loyalty_points_bonus_config: false,
    min_spend_products: false,
    min_user_used_limit: false,
  };

  const discountsToUse =
    promoCode?.promo_discounts && promoCode.promo_discounts.length > 0
      ? promoCode.promo_discounts
      : promoDiscountData || [];

  discountsToUse.forEach((d) => {
    if (d.discount_value > 0) {
      model.discount_value = true;
    }
    if (d.min_spend > 0) {
      model.min_spend = true;
    }
    if (d.max_discount > 0) {
      model.max_discount = true;
    }
    if (d.subscription_item_discount_value > 0) {
      model.subscription_item_discount_value = true;
    }
    if (d.subscription_item_discount_type !== 'none') {
      model.subscription_item_discount_type = true;
    }
    if (d.min_spend_products && d.min_spend_products.length > 0) {
      model.min_spend_products = true;
    }
    if (d.min_user_used_limit && d.min_user_used_limit > 0) {
      model.min_user_used_limit = true;
    }
    if (d.discount_value > 0) {
      model.discount_value = true;
    }
    if (d.products?.filter((p) => p !== 'ALL-ALL')?.length > 0) {
      model.products = true;
    }

    if (d.loyalty_points_bonus_config && d.loyalty_points_bonus_config.loyaltyPointsBonusConfigV1?.length > 0) {
      model.loyalty_points_bonus_config = true;
    }
  });

  if (promoCode?.promo_conditions?.filter((c) => c.promo_code_discount_ids).length > 0) {
    model.conditions = true;
  }
  return model;
};

interface Props {
  promoCode: InternalPromoWithDiscounts;
  promoWarnings: Array<PromoWarning>;
  parentPromo?: InternalPromoWithDiscounts | null;
  isPromoAddDiscountView?: boolean;
  promoLoyaltyBonusApplyMethod: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'] | null;
  promoType: string;
  promoDiscountData: Array<App.PromoDiscount> | null;
  setPromoDiscountData: (discounts: Array<App.PromoDiscount>) => void;
  promoProductTypes: PromoProductTypes;
  promoRegionGroupings: PromoRegionGroupings;
  promoTypes: PromoMeta['promo_types'];
  promoLoyaltyMetaConfig: PromoLoyaltyMetaConfig;
  promoMetaLoading: boolean;
  rerender: () => void | Promise<void>;
  highlightedDiscountIds?: Array<string> | 'all';
  onDiscountRowSelect: (discountId: string) => void;
  onDiscountCopySaved?: (params: { sourceDiscountId: string; copiedDiscountId: string }) => Promise<void> | void;
  showCopyAction?: boolean;
}

type DiscountObjectFormRow = InternalPromoWithDiscounts['promo_discounts'][0] & {
  isNew: boolean;
  isRegionGroupDiscount: boolean;
  id: string;
};

function PromoDiscountFormV4({
  promoCode,
  promoWarnings,
  parentPromo,
  promoLoyaltyBonusApplyMethod,
  promoType,
  promoDiscountData,
  setPromoDiscountData,
  promoProductTypes,
  promoRegionGroupings,
  promoTypes,
  promoLoyaltyMetaConfig,
  promoMetaLoading,
  rerender,
  highlightedDiscountIds,
  onDiscountRowSelect,
  onDiscountCopySaved,
  showCopyAction = false,
}: Props) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<App.PromoDiscount | null>(null);
  const [isNewDiscount, setIsNewDiscount] = useState(false);
  const [copiedFromDiscountId, setCopiedFromDiscountId] = useState<string | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery();
  // showAllProducts of true will show every possible product type option in the products type dropdown (including 'preferToHide' options)
  const showAllProducts = !!query.get('showAllProducts');

  // Helper function to get consistent row ID
  const getRowId = useCallback((row: any) => {
    return (
      row.id_promo_code_discount ||
      row.id ||
      `${row.id_promo_code_discount}-${row.id}-${row.region}-${row.products.join('-')}`
    );
  }, []);

  const handleAddDiscount = () => {
    const id = uuid();

    const newDiscount: App.PromoDiscount = {
      id,
      id_promo_code: promoCode?.id_promo_code,
      id_promo_code_discount: id,
      products: ['ALL-ALL'],
      region: 'AU',
      subscription_item_discount_type: 'none',
      isNew: true,
      min_spend_products: [],
      discount_value: 0,
      max_discount: 0,
      min_spend: 0,
      modified_by: '',
      created_at: '',
      updated_at: '',
      loyalty_points_bonus_config: undefined,
    };

    setEditingDiscount(newDiscount);
    setIsNewDiscount(true);
    setCopiedFromDiscountId(null);
    setEditModalOpen(true);
  };

  const handleEditDiscount = useCallback((discount: App.PromoDiscount) => {
    setEditingDiscount(discount);
    setIsNewDiscount(false);
    setCopiedFromDiscountId(null);
    setEditModalOpen(true);
  }, []);

  const handleCopyDiscount = useCallback((discount: App.PromoDiscount) => {
    setEditingDiscount(copyPromoDiscount(discount));
    setIsNewDiscount(true);
    setCopiedFromDiscountId(discount.id_promo_code_discount);
    setEditModalOpen(true);
  }, []);

  const handleDeletePromoDiscount = useCallback(
    async (discount: App.PromoDiscount) => {
      if (!promoCode) {
        setPromoDiscountData(
          promoDiscountData.filter((row) => row.id_promo_code_discount !== discount.id_promo_code_discount),
        );
        return;
      }

      let response;
      try {
        response = await deletePromoDiscount(discount.id_promo_code, discount.id_promo_code_discount);

        if (response.status == 200) {
          enqueueSnackbar('Discount deleted successfully', {
            variant: 'success',
          });
          if (rerender) {
            rerender();
          }
        } else {
          enqueueSnackbar(
            `Error deleting discount - ${'message' in response.body ? response.body.message : ''} - ${JSON.stringify(
              response.body,
            )}`,
            {
              variant: 'error',
            },
          );
        }
      } catch (err) {
        enqueueSnackbar(`Error deleting discount - ${JSON.stringify({ err, response }, null, 4)}`, {
          variant: 'error',
        });
      }
    },
    [promoCode, promoDiscountData, enqueueSnackbar, setPromoDiscountData, rerender],
  );

  const handleSaveDiscount = useCallback(
    async (discount: App.PromoDiscount) => {
      const updatedRow = { ...discount, isNew: false } as App.PromoDiscount;
      let didSaveDiscount = false;
      let shouldRerender = false;

      try {
        const payload: definitions['Internal Promo Discount'] = {
          id_promo_code: discount.id_promo_code,
          id_promo_code_discount: discount.id_promo_code_discount,
          discount_value: discount.discount_value,
          max_discount: discount.max_discount,
          min_spend: discount.min_spend,
          products: updatedRow.products,
          subscription_item_discount_value: +discount.subscription_item_discount_value,
          subscription_item_discount_type: discount.subscription_item_discount_type,
          region: discount.region,
          min_spend_products: discount.min_spend_products,
          min_user_used_limit: discount.min_user_used_limit,
          loyalty_points_bonus_config: mapLoyaltyPointsBonusConfigToV1(discount.loyalty_points_bonus_config),
        };

        // promoCode exists means this is not the create form -> update form.
        // We should only create or update promo discount when it is update form.
        // Promo Discount in the create form will be created only when promo code has been created.
        if (promoCode) {
          const results = await createOrUpdatePromoDiscount(payload);
          if (results.status == 200) {
            enqueueSnackbar(results.message ?? 'Discount updated successfully', {
              variant: 'success',
            });
            didSaveDiscount = true;
            setPromoDiscountData(
              promoDiscountData.map((row) =>
                row.id_promo_code_discount === discount.id_promo_code_discount ? updatedRow : row,
              ),
            );
            shouldRerender = true;
          } else {
            enqueueSnackbar('Discount not found', {
              variant: 'error',
            });
          }
        } else {
          didSaveDiscount = true;
          if (isNewDiscount) {
            setPromoDiscountData([...promoDiscountData, updatedRow]);
          } else {
            setPromoDiscountData(
              promoDiscountData.map((row) =>
                row.id_promo_code_discount === discount.id_promo_code_discount ? updatedRow : row,
              ),
            );
          }
        }

        if (didSaveDiscount && copiedFromDiscountId) {
          await onDiscountCopySaved?.({
            sourceDiscountId: copiedFromDiscountId,
            copiedDiscountId: updatedRow.id_promo_code_discount,
          });
        }

        if (shouldRerender) {
          await rerender();
        }

        setEditModalOpen(false);
        setEditingDiscount(null);
        setIsNewDiscount(false);
        setCopiedFromDiscountId(null);
      } catch (err) {
        console.error(`processRowUpdate DiscountFormV3`, {
          message: err.message,
          stack: err.stack,
          id: discount.id_promo_code_discount,
          row: discount,
        });
        enqueueSnackbar(err?.message, {
          autoHideDuration: 5000,
        });
      }
    },
    [
      promoCode,
      promoDiscountData,
      setPromoDiscountData,
      enqueueSnackbar,
      rerender,
      isNewDiscount,
      copiedFromDiscountId,
      onDiscountCopySaved,
    ],
  );

  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingDiscount(null);
    setIsNewDiscount(false);
    setCopiedFromDiscountId(null);
  }, []);

  useEffect(() => {
    let hiddenFields: Array<string> = [];

    getProductTypeOptions(promoProductTypes, []).forEach((option) => {
      const matchingProductOptions = promoDiscountData.filter(
        (pd) => showAllProducts || pd.products.includes(option.value),
      );
      if (option?.preferToHide && matchingProductOptions.length === 0) {
        hiddenFields.push(option.value);
      } else {
        hiddenFields = hiddenFields.filter((hf) => hf !== option.value);
      }
    });
  }, [query, promoDiscountData, showAllProducts, promoProductTypes]);

  const getProductBKOption = (
    optionValue: string,
    promoProductTypes: PromoProductTypes,
    filterType: 'min_spend_products' | 'products',
  ) => {
    let match = null;
    switch (filterType) {
      case 'products':
        match = getProductTypeOptions(promoProductTypes, []).filter((o) => o.value === optionValue);
        break;
      case 'min_spend_products':
        match = getMinSpendProductTypeOptions(promoProductTypes, []).filter((o) => o.value === optionValue);
        break;
      default:
        console.warn(`Could not get getProductBKOption for value ${optionValue} and filterType ${filterType}`);
        break;
    }
    if (match && match.length > 0) {
      return {
        label: match[0].label,
        value: match[0].value,
      };
    } else {
      console.error('Fallback productBKOptions used, no display name found for productBK: ' + optionValue);
      return {
        label: optionValue,
        value: optionValue,
      };
    }
  };

  const SHOW_PRODUCT_LABELS_THRESHOLD = 5;

  const getColumns = (
    promoConditions: Array<definitions['PromoCodeCondition']>,
  ): Array<GridColDef<DiscountObjectFormRow>> => {
    return [
      {
        field: 'region',
        headerName: 'Region',
        flex: 1,
        renderCell: (params) => {
          const region = getRegionOptions(promoRegionGroupings).find((r) => r.code === params.row.region);
          return <Typography title={region?.message}>{region?.name || params.row.region}</Typography>;
        },
      },
      {
        field: 'products',
        headerName: 'Products',
        flex: 1,
        renderCell: (params) => {
          const productTitle =
            params.row.products?.length == 0
              ? 'None'
              : `${params.row.products
                  .map((p) => getProductBKOption(p, promoProductTypes, 'products'))
                  .map((o) => o.label)
                  .join('\n')}`;

          const allLabels = params.row.products.map((p) => getProductBKOption(p, promoProductTypes, 'products'));

          const showSummaryLabel = allLabels.length == 0 || allLabels.length >= SHOW_PRODUCT_LABELS_THRESHOLD;
          if (showSummaryLabel) {
            return (
              <Typography title={productTitle}>
                {allLabels?.length == 0 ? `None` : `${allLabels.length} Products Selected`}
              </Typography>
            );
          }

          return (
            <Box title={productTitle}>
              {allLabels.map((p) => (
                <Typography key={`${p.label}_${p.value}`}>{p.label}</Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: 'discount_value',
        headerName: `${getPromoTypeDisplayName(promoType)} Discount`,
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'max_discount',
        headerName: 'Max Discount',
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'subscription_item_discount_value',
        headerName: 'Subscription Discount',
        type: 'number',
        renderCell: (params) => {
          if (params.row.subscription_item_discount_type === 'none') {
            if (params.row.subscription_item_discount_value == 0) {
              return null;
            } else {
              return (
                <Typography sx={{ textDecoration: 'line-through' }}>
                  {params.row.subscription_item_discount_value}{' '}
                </Typography>
              );
            }
          }
          if (params.row.subscription_item_discount_type === 'percentage') {
            return `${params.row.subscription_item_discount_value}%`;
          }

          return currencyFormatter(
            getCurrencyCodeByRegionCode(params.row.region) ?? 'AUD',
            params.row.subscription_item_discount_value,
          );
        },
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'subscription_item_discount_type',
        headerName: 'Subscription Type',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params) => {
          return getPromoTypeDisplayName(params.row.subscription_item_discount_type);
        },
      },
      {
        field: 'loyalty_points_bonus_config',
        headerName: 'Loyalty Points',
        type: 'number',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params) => {
          return (
            <LoyaltyPointsBonusConfigSummary
              loyaltyBonusApplyMethod={promoLoyaltyBonusApplyMethod}
              isPromoMetaLoading={promoMetaLoading}
              promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
              config={mapLoyaltyPointsBonusConfigToV1(params.row.loyalty_points_bonus_config)}
            />
          );
        },
      },
      {
        field: 'min_spend',
        headerName: 'Min Spend',
        type: 'number',
        renderHeader: () => (
          <HeaderInfo
            title="[Discount Restriction] - Min Spend - When set, only orders with this level of minimum spend will qualify for this discount. [See also 'Min Spend Product Types']"
            header="Min Spend"
            iconType="tick"
          />
        ),
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'conditions',
        headerName: 'Conditions',
        flex: 1,
        renderCell: (params) => {
          if (!promoConditions || promoConditions.length == 0) {
            return null;
          }
          const matches = promoConditions.filter(
            (c) =>
              !c.promo_code_discount_ids ||
              c.promo_code_discount_ids.length == 0 ||
              c.promo_code_discount_ids.includes(params.row.id_promo_code_discount),
          );

          const title = `All ${matches.length} conditions must be met to qualify for this discount:`;
          return (
            <DebugModal
              title={title}
              type="generic"
              data={{
                ...(params.row.products && params.row.products.length > 0
                  ? { onlyValidForProductTypes: params.row.products }
                  : {}),
                ...(params.row.min_spend ? { minSpend: params.row.min_spend } : {}),
                ...(params.row.min_spend_products && params.row.min_spend_products.length > 0
                  ? { minSpendProducts: params.row.min_spend_products }
                  : {}),
                ...(params.row.min_user_used_limit ? { minUserUsedLimit: params.row.min_user_used_limit } : {}),
                ...(matches.length > 0
                  ? {
                      conditions: matches.reduce((acc, c) => {
                        acc[
                          `${c.condition_type} ${
                            c.condition_apply_type === 'NONE' || !c.condition_apply_type
                              ? ''
                              : `-- ${c.condition_apply_type} --`
                          } "${c.condition_value}"`
                        ] = c;
                        return acc;
                      }, {}),
                    }
                  : {}),
              }}
            />
          );
        },
      },
      {
        field: 'min_spend_products',
        headerName: 'Min Spend Products',
        flex: 2,
        renderHeader: () => (
          <HeaderInfo
            title={getProductTypeEligibilityHelperTitle(promoProductTypes, 'minSpend')}
            header="Min Spend Products"
            iconType="tick"
          />
        ),
        renderCell: (params: GridRenderCellParams<DiscountObjectFormRow>) => {
          if (!params.row.min_spend_products) {
            return null;
          }

          const productTitle =
            params.row.min_spend_products?.length == 0
              ? 'None'
              : `${params.row.min_spend_products
                  .map((p) => getProductBKOption(p, promoProductTypes, 'min_spend_products'))
                  .map((o) => `${o.label}`)
                  .join('\n')}`;

          const allLabels = params.row.min_spend_products.map((p) =>
            getProductBKOption(p, promoProductTypes, 'min_spend_products'),
          );

          const showSummaryLabel = allLabels.length == 0 || allLabels.length >= SHOW_PRODUCT_LABELS_THRESHOLD;
          if (showSummaryLabel) {
            return (
              <Typography title={productTitle}>
                {allLabels?.length == 0 ? `None` : `${allLabels.length} Products Selected`}
              </Typography>
            );
          }

          return (
            <Box title={productTitle}>
              {allLabels.map((p) => (
                <Typography key={`${p.label}_${p.value}`}>{p.label}</Typography>
              ))}
            </Box>
          );
        },
      },
      {
        field: 'min_user_used_limit',
        headerName: 'Min User Used Limit',
        type: 'number',
        renderHeader: () => (
          <HeaderInfo
            title="[Discount Restriction] - Min User Used Limit - When set, this promo must be used this many times before a user qualifies for this discount"
            header="Min User Used Limit"
            iconType="tick"
          />
        ),
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<DiscountObjectFormRow>) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleEditDiscount(params.row)} title="Edit discount">
              <EditIcon />
            </IconButton>
            {showCopyAction && (
              <IconButton
                aria-label="Copy discount"
                size="small"
                onClick={() => handleCopyDiscount(params.row)}
                title="Copy discount"
              >
                <ContentCopyIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                if (confirm('Are you sure you want to delete this discount row?')) {
                  handleDeletePromoDiscount(params.row);
                }
              }}
              title="Delete discount"
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
        headerAlign: 'center',
        align: 'center',
      },
    ];
  };

  useEffect(() => {
    setPromoDiscountData(promoCode?.promo_discounts || []);
  }, [promoCode?.promo_discounts, setPromoDiscountData]);

  useEffect(() => {
    setColumnVisibilityModel(getPromoDiscountColumnVisibilityModel(promoCode, promoDiscountData));
  }, [promoCode, promoDiscountData]);

  if (promoMetaLoading) {
    return <Spinner />;
  }

  const getRowClassName = (params: GridRowClassNameParams<DiscountObjectFormRow>): string => {
    if (highlightedDiscountIds === 'all') {
      return 'highlighted-row';
    }
    if (highlightedDiscountIds?.includes(params.row.id_promo_code_discount)) {
      return 'highlighted-row';
    }

    if (!promoWarnings) {
      return '';
    }

    const error = promoWarnings?.filter(
      (pw) =>
        params?.row?.id_promo_code_discount === pw.id_promo_code_discount ||
        pw.id_promo_code_discounts?.includes(params?.row?.id_promo_code_discount),
    );

    if (error?.length == 0) {
      return '';
    }

    if (error[0].level == 'error') {
      return 'conflicting-row';
    }

    if (error[0].level == 'warning') {
      return 'warning-row';
    }

    console.warn('PromoDiscountFormV4:getRowClassName:unknown-error-level', error);
    return 'warning-row';
  };

  return (
    <Box>
      <Stack direction="column">
        <PageSubheader title="Discounts">
          <Stack direction="row" gap={2} mb={1}>
            <Button onClick={() => handleAddDiscount()} variant="contained" size="small" startIcon={<AddIcon />}>
              Add Discount
            </Button>
          </Stack>
        </PageSubheader>
      </Stack>
      {promoDiscountData && (
        <Box mt={3}>
          <DataGrid
            onRowClick={(params) => onDiscountRowSelect(params.row.id_promo_code_discount)}
            columns={getColumns(promoCode?.promo_conditions)}
            sx={{
              '.MuiDataGrid-row.highlighted-row': {
                bgcolor: 'primary.light',
                '&:hover': {
                  border: '1px solid primary.main',
                },
              },
              '.MuiDataGrid-row.conflicting-row': {
                border: '1px solid error.light',
                '&:hover': {
                  border: '1px solid error.main',
                },
              },
              '.MuiDataGrid-row.warning-row': {
                border: '1px solid warning.light',
                '&:hover': {
                  border: '1px solid warning.main',
                },
              },
            }}
            getRowClassName={getRowClassName}
            autoHeight
            rows={promoDiscountData || []}
            getRowId={getRowId}
            pageSizeOptions={[100]}
            initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
            disableColumnSorting={false}
            getRowHeight={() => 'auto'}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          />
        </Box>
      )}

      <PromoDiscountEditModal
        open={editModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveDiscount}
        discount={editingDiscount}
        isNew={isNewDiscount}
        promoType={promoType}
        promoProductTypes={promoProductTypes}
        promoRegionGroupings={promoRegionGroupings}
        promoTypes={promoTypes}
        promoCode={promoCode}
        promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
        isPromoMetaLoading={promoMetaLoading}
        promoLoyaltyBonusApplyMethod={promoLoyaltyBonusApplyMethod}
      />

      <Accordion title="Discounting rules" defaultExpanded={false}>
        <Stack direction="column" gap={1.5}>
          <Stack direction="column" gap={0.5}>
            <Typography variant="subtitle2">Promo discount calculation</Typography>
            <Typography variant="caption">
              Checkout runs /discount with the current cart/order payload, code name, user, request source, and any
              voucher ids. The request and response are saved for the Promo Requests admin view, including the first
              error code/message when the calculation fails.
            </Typography>
            <Typography variant="caption">
              Each discount row above is evaluated against the order. Products define which items can receive a
              discount; Min Spend Products define which items are counted against the row's Min Spend value.
            </Typography>
            <Typography variant="caption">
              If multiple discount rows qualify, the calculator works out the value of each row and applies the best
              matching row for each item, with max discount caps and fixed amount limits applied before the response is
              returned.
            </Typography>
            <Typography variant="caption">
              Payment logging runs /log after the order is placed. It reloads the real order, maps it back into a
              discount request order, recalculates in log mode, compares the client amount with the server amount, then
              writes Promo Logs or Promo Failures for admin visibility.
            </Typography>
          </Stack>

          <Stack direction="column" gap={0.5}>
            <Typography variant="subtitle2">Condition details</Typography>
            <Typography variant="caption">
              Promo-wide validation checks expiry, approval mode, code usage limits, currency/region availability, order
              min spend, email allowlists/domains, per-user and yearly limits, device type, first purchase rules,
              employee rules, airline/offer restrictions, and legacy promo-user eligibility.
            </Typography>
            <Typography variant="caption">
              Promo conditions can filter specific discount rows when their Discount Filter is set. Without a Discount
              Filter, a failed condition blocks the whole promo code.
            </Typography>
            <Typography variant="caption">
              Product, min spend, destination, occupancy, booking date, margin, provider, fare, flight, place id,
              subscriber tier, loyalty offer type, and spoofing conditions can all remove matching rows or block the
              promo, depending on their configuration.
            </Typography>
            <Typography variant="caption">
              Voucher conditions are evaluated during /discount for display and application. Voucher redemption is
              persisted separately through /vouchers/log, where voucher user ownership, reward amount, currency,
              remaining balance, partial redemption, and provide-credit fields are revalidated before Voucher Logs are
              written.
            </Typography>
          </Stack>
        </Stack>
      </Accordion>
    </Box>
  );
}

export default PromoDiscountFormV4;
